import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { ModalComponent } from '../../shared/modal.component';
import { InfoModalComponent } from '../../shared/info-modal.component';
import { UserService } from '../../services/user.service';
import { PagerService } from '../../services/pager.service';
import {
  TicketReassignService,
  AttentionExportScope,
  IReassignAttention,
  IReassignBatch,
  IReassignCommitResult,
  IReassignHistoryFilters,
  IReassignHistoryQuery,
  IReassignPageInfo,
  IReassignPreview,
  IReassignRequest,
  IReassignSource,
  IReassignUndoResult,
  IReassignUserRef,
  ReassignStatus
} from '../../services/ticket-reassign.service';

/** A New / Active / Closed checkbox on the reassignment form. */
interface IStatusOption {
  value: ReassignStatus;
  label: string;
  checked: boolean;
}

const ROLE_NAMES: { [roleType: number]: string } = {
  2: 'Client user',
  3: 'Partner',
  6: 'Attorney',
  8: 'Admin'
};

const REFRESHED_NOTE = 'The accounts and history on this page have been refreshed.';
const REASON_MIN = 20;
const REASON_MAX = 500;
const HISTORY_PER_PAGE = 10;
const MAX_SOURCES = 50;
const CONFIRM_LIST_LIMIT = 10;
const FILTER_DEBOUNCE_MS = 300;

const EXPORT_FALLBACK_FILENAME = 'ticket-reassignments.csv';

declare var require: any;

const STATUS_NAMES: { [status: number]: string } = {
  1: 'New',
  2: 'Active',
  3: 'Closed'
};

/** System Admin screen to move every ticket from one account to another attorney, with preview, confirmation and undo. */
@Component({
    selector: 'app-ticket-reassign',
    templateUrl: './ticket-reassign.component.html',
    styleUrls: ['./ticket-reassign.component.scss'],
    standalone: false
})
export class TicketReassignComponent implements OnInit, OnDestroy {
  authorised = false;
  crumbList: any[];

  sources: IReassignSource[] = [];
  sourcesLoading = false;
  sourcesError: string = null;

  targets: IReassignUserRef[] = [];
  targetsLoading = false;
  targetsError: string = null;

  history: IReassignBatch[] = [];
  historyLoading = false;
  historyError: string = null;
  historyPage = 1;
  historyPageInfo: IReassignPageInfo = null;
  pager: any = {};
  exporting = false;
  // Filters behind the rows on screen, so the export matches them even mid-debounce.
  private historyAppliedFilters: IReassignHistoryFilters = null;

  // History filters; dates are the yyyy-mm-dd strings from the date inputs.
  filterDateFrom = '';
  filterDateTo = '';
  filterFrom = '';
  filterTo = '';
  filterState: 'done' | 'undone' | null = null;
  filterStatus: ReassignStatus | null = null;

  attention: IReassignAttention = null;
  attentionLoading = false;
  attentionError: string = null;
  // Scope of the attention CSV being prepared; one download at a time.
  attentionDownloading: AttentionExportScope = null;
  private attentionSeq = 0;

  // Ticked source account ids; the search box only hides rows, it never unticks them.
  private selectedIds = new Set<number>();
  sourceSearch = '';
  visibleRealSources: IReassignSource[] = [];
  visibleDeletedSources: IReassignSource[] = [];
  readonly maxSources = MAX_SOURCES;
  toUserId: number = null;
  statusOptions: IStatusOption[] = this.defaultStatusOptions();
  // Not part of the preview: editing it must not clear one.
  reason = '';
  readonly reasonMin = REASON_MIN;
  readonly reasonMax = REASON_MAX;

  preview: IReassignPreview = null;
  previewRequest: IReassignRequest = null;
  previewLoading = false;
  previewError: string = null;

  committing = false;
  awaitingConfirm = false;
  undoingId: number = null;

  // Bumped whenever the selection changes so an in-flight preview response is discarded.
  private previewSeq = 0;
  // Bumped on every history request so only the latest response is applied.
  private historySeq = 0;
  private textFilter$ = new Subject<void>();
  private textFilterSub: Subscription = null;

  /** Injects the API, user, modal and router services. */
  constructor(
    private reassignSer: TicketReassignService,
    public userSer: UserService,
    private modalService: NgbModal,
    private pagerS: PagerService,
    private ngZone: NgZone,
    private router: Router) {
  }

  /** Sends non-sysadmins away, otherwise loads the lists. */
  ngOnInit(): void {
    if (!this.userSer.isAdmin()) {
      this.displayMessage('Heads Up', 'You lack permission to this feature.');
      this.router.navigate(['/dashboard']);
      return;
    }
    this.authorised = true;
    this.userSer.selectMenu('admin-sidebar');

    this.crumbList = [
      { title: 'Home', url: '/dashboard' },
      { title: 'Admin Menu', url: '/admins/admin-menu' }
    ];

    this.textFilterSub = this.textFilter$
      .pipe(debounceTime(FILTER_DEBOUNCE_MS))
      .subscribe(() => this.applyFilters());

    this.loadSources();
    this.loadTargets();
    this.loadHistory();
    this.loadAttention();
  }

  /** Stops the filter debounce subscription. */
  ngOnDestroy(): void {
    if (this.textFilterSub) {
      this.textFilterSub.unsubscribe();
    }
  }

  /** True while any write or confirmation dialog is in progress. */
  get busy(): boolean {
    return this.committing || this.awaitingConfirm || this.undoingId !== null;
  }

  /** Number of ticked source accounts, including any hidden by the search. */
  get selectedCount(): number {
    return this.selectedIds.size;
  }

  /** Ticked source accounts, in list order. */
  selectedSources(): IReassignSource[] {
    return this.sources.filter(s => this.selectedIds.has(s.UserID));
  }

  /** Targets that can receive the tickets, excluding every ticked source account. */
  get availableTargets(): IReassignUserRef[] {
    return this.targets.filter(t => !this.selectedIds.has(t.UserID));
  }

  /** Number of status checkboxes currently ticked. */
  get checkedCount(): number {
    return this.statusOptions.filter(o => o.checked).length;
  }

  /** Loads users who currently have tickets assigned. */
  loadSources(): void {
    this.sourcesLoading = true;
    this.sourcesError = null;
    this.reassignSer.getSources()
      .pipe(finalize(() => this.sourcesLoading = false))
      .subscribe({
        next: (data) => {
          this.sources = data || [];
          const present = new Set(this.sources.map(s => s.UserID));
          let dropped = false;
          this.selectedIds.forEach(id => {
            if (!present.has(id)) {
              this.selectedIds.delete(id);
              dropped = true;
            }
          });
          if (dropped) {
            this.clearPreview();
          }
          this.refreshVisibleSources();
        },
        error: (err) => {
          this.sources = [];
          this.refreshVisibleSources();
          this.sourcesError = this.serverMessage(err, 'Unable to load the users who have tickets assigned.');
        }
      });
  }

  /** Loads the attorneys tickets can be moved to. */
  loadTargets(): void {
    this.targetsLoading = true;
    this.targetsError = null;
    this.reassignSer.getTargets()
      .pipe(finalize(() => this.targetsLoading = false))
      .subscribe({
        next: (data) => this.targets = data || [],
        error: (err) => {
          this.targets = [];
          this.targetsError = this.serverMessage(err, 'Unable to load the list of attorneys.');
        }
      });
  }

  /** Loads the current history page with the current filters, keeping the rows on screen until it arrives. */
  loadHistory(): void {
    const seq = ++this.historySeq;
    if (this.dateRangeInvalid()) {
      this.historyLoading = false;
      return;
    }
    const query: IReassignHistoryQuery = {
      page: this.historyPage,
      per_page: HISTORY_PER_PAGE,
      date_from: this.dayBoundary(this.filterDateFrom, false),
      date_to: this.dayBoundary(this.filterDateTo, true),
      from: this.filterFrom,
      to: this.filterTo,
      state: this.filterState,
      status: this.filterStatus
    };
    this.historyLoading = true;
    this.historyError = null;
    this.reassignSer.getHistory(query)
      .pipe(finalize(() => {
        if (seq === this.historySeq) {
          this.historyLoading = false;
        }
      }))
      .subscribe({
        next: (res) => {
          if (seq !== this.historySeq) {
            return;
          }
          this.history = res.rows;
          this.historyPageInfo = res.page || null;
          this.historyAppliedFilters = {
            date_from: query.date_from,
            date_to: query.date_to,
            from: query.from,
            to: query.to,
            state: query.state,
            status: query.status
          };
          const info = this.historyPageInfo;
          this.historyPage = info && info.page ? info.page : query.page;
          this.pager = info ? this.pagerS.getPager(info.total, this.historyPage, info.per_page || HISTORY_PER_PAGE) : {};
        },
        error: (err) => {
          if (seq !== this.historySeq) {
            return;
          }
          this.history = [];
          this.historyPageInfo = null;
          this.historyAppliedFilters = null;
          this.pager = {};
          this.historyError = this.serverMessage(err, 'Unable to load the reassignment history.');
        }
      });
  }

  /** Moves to another history page, keeping the filters. */
  setHistoryPage(page: number): void {
    const last = this.historyPageInfo ? this.historyPageInfo.total_pages : 1;
    if (page < 1 || page > last || page === this.historyPage) {
      return;
    }
    this.historyPage = page;
    this.loadHistory();
  }

  /** Queues a debounced reload after typing in a From or To filter box. */
  onTextFilterChange(): void {
    this.textFilter$.next();
  }

  /** Reloads immediately after a select or date filter changes. */
  onFilterChange(): void {
    this.applyFilters();
  }

  /** Goes back to page 1 and reloads with the current filters. */
  applyFilters(): void {
    this.historyPage = 1;
    this.loadHistory();
  }

  /** Empties every history filter and reloads. */
  clearFilters(): void {
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.filterFrom = '';
    this.filterTo = '';
    this.filterState = null;
    this.filterStatus = null;
    this.applyFilters();
  }

  /** True when any history filter has a value. */
  hasFilters(): boolean {
    return !!(this.filterDateFrom || this.filterDateTo || this.filterFrom.trim() || this.filterTo.trim() ||
      this.filterState || this.filterStatus);
  }

  /** True when both dates are set and From is after To; the inputs' yyyy-mm-dd strings compare correctly as text. */
  dateRangeInvalid(): boolean {
    return !!this.filterDateFrom && !!this.filterDateTo && this.filterDateFrom > this.filterDateTo;
  }

  /** Start (or end, 23:59:59.999) of a local yyyy-mm-dd day as an ISO UTC instant; null when blank or unparseable. */
  private dayBoundary(day: string, endOfDay: boolean): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || '');
    if (!match) {
      return null;
    }
    const date = endOfDay
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999)
      : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0);
    return isNaN(date.getTime()) ? null : date.toISOString();
  }

  /** True when the loaded history has rows to export and no download or reload is running. */
  canExport(): boolean {
    return !this.exporting && !this.historyLoading && !this.dateRangeInvalid() && !this.historyError &&
      !!this.historyAppliedFilters && !!this.historyPageInfo && this.historyPageInfo.total > 0;
  }

  /** Downloads every history row matching the applied filters as a CSV file. */
  downloadCsv(): void {
    if (!this.canExport()) {
      return;
    }
    this.exporting = true;
    this.runDownload(this.reassignSer.exportHistory(this.historyAppliedFilters), EXPORT_FALLBACK_FILENAME,
      'Unable to download the history.', () => this.exporting = false);
  }

  /** Saves a CSV response under the server's filename, or shows the refusal under "Download Not Completed". */
  private runDownload(request: Observable<HttpResponse<Blob>>, fallbackFilename: string, fallbackMessage: string,
                      done: () => void): void {
    request
      .pipe(finalize(done))
      .subscribe({
        next: (response) => {
          const body = response ? response.body : null;
          if (!body) {
            this.displayMessage('Download Not Completed', fallbackMessage);
            return;
          }
          if ((body.type || '').indexOf('json') !== -1) {
            this.showDownloadError(body, fallbackMessage);
            return;
          }
          this.saveBlob(body, this.exportFilename(response.headers.get('Content-Disposition'), fallbackFilename));
        },
        error: (err) => {
          const httpErr = err && err.status === false ? err.error : err;
          this.showDownloadError(httpErr ? httpErr.error : null, fallbackMessage);
        }
      });
  }

  /** Saves a blob through a temporary link, as edit-ticket does; require('file-saver') fails on the dev server. */
  private saveBlob(blob: Blob, filename: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
  }

  /** Filename from a Content-Disposition header, or the fallback when it is missing or not exposed. */
  private exportFilename(disposition: string | null, fallback: string): string {
    if (!disposition) {
      return fallback;
    }
    const extMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
    const raw = extMatch ? extMatch[1] : (plainMatch ? plainMatch[1] : null);
    if (!raw) {
      return fallback;
    }
    try {
      return decodeURIComponent(raw.trim());
    } catch {
      return raw.trim();
    }
  }

  /** Shows the server's message from a download error body, which arrives as a Blob of JSON. */
  private showDownloadError(body: any, fallback: string): void {
    const show = (msg: string) => this.ngZone.run(() => this.displayMessage('Download Not Completed', msg));
    if (body instanceof Blob) {
      // Blob.text() resolves outside Angular's zone, hence ngZone.run in show().
      body.text()
        .then(text => {
          const parsed = JSON.parse(text);
          const msg = parsed && typeof parsed.message === 'string' && parsed.message.trim() !== '' ? parsed.message : fallback;
          show(msg);
        })
        .catch(() => show(fallback));
      return;
    }
    show(body && typeof body.message === 'string' && body.message.trim() !== '' ? body.message : fallback);
  }

  /** Loads the attention counts on their own request, so the form never waits for them. */
  loadAttention(): void {
    const seq = ++this.attentionSeq;
    this.attentionLoading = true;
    this.attentionError = null;
    this.reassignSer.getAttention()
      .pipe(finalize(() => {
        if (seq === this.attentionSeq) {
          this.attentionLoading = false;
        }
      }))
      .subscribe({
        next: (data) => {
          if (seq !== this.attentionSeq) {
            return;
          }
          if (!data || !data.hidden || !data.hidden.open || !data.deleted_accounts) {
            this.attention = null;
            this.attentionError = 'Unable to check for tickets needing attention.';
            return;
          }
          this.attention = data;
        },
        error: (err) => {
          if (seq !== this.attentionSeq) {
            return;
          }
          this.attention = null;
          this.attentionError = this.serverMessage(err, 'Unable to check for tickets needing attention.');
        }
      });
  }

  /** True while checking, on an error, or when there is anything hidden or assigned to a deleted account. */
  showAttentionCard(): boolean {
    if (this.attentionError || (this.attentionLoading && !this.attention)) {
      return true;
    }
    return !!this.attention && (this.attention.hidden.total > 0 || this.attention.deleted_accounts.tickets > 0);
  }

  /** Plain sentence describing tickets with missing records, open first, then closed and those with no status. */
  hiddenSentence(): string {
    const h = this.attention.hidden;
    const open = h.open.total;
    const pointsTo = (n: number) => (n === 1 ? ' points' : ' point') + ' to a requester, subject or client that no longer exists.';
    const parts: string[] = [];
    if (open > 0) {
      parts.push(this.formatCount(open) + ' open ' + this.ticketWord(open) + ' (' + this.formatCount(h.open.new) + ' new, ' +
        this.formatCount(h.open.active) + ' active)' + pointsTo(open));
    }
    const rest = (h.closed || 0) + (h.other || 0);
    if (rest > 0) {
      let what = '';
      if (h.closed > 0) {
        what = this.formatCount(h.closed) + ' closed ' + this.ticketWord(h.closed);
        if (h.other > 0) {
          what += ' and ' + this.formatCount(h.other) + ' with no status';
        }
      } else {
        what = this.formatCount(h.other) + ' ' + this.ticketWord(h.other) + ' with no status';
      }
      parts.push(open > 0
        ? 'A further ' + what + (rest === 1 ? ' is' : ' are') + ' in the same position.'
        : what.charAt(0).toUpperCase() + what.slice(1) + pointsTo(rest));
    }
    const shown = open + rest;
    if (shown > 0) {
      parts.push((shown === 1 ? 'It is' : 'They are') + ' shown in ticket lists with placeholders such as Unknown requester.');
    }
    return parts.join(' ');
  }

  /** Plain sentence describing tickets assigned to accounts that no longer exist. */
  deletedAccountsSentence(): string {
    const d = this.attention.deleted_accounts;
    return this.formatCount(d.tickets) + ' ' + this.ticketWord(d.tickets) + ' (' + this.formatCount(d.open) + ' open) ' +
      (d.tickets === 1 ? 'is' : 'are') + ' assigned to ' + this.formatCount(d.accounts) + ' ' +
      (d.accounts === 1 ? 'account that no longer exists' : 'accounts that no longer exist') +
      '. Nobody is notified when a client replies on ' + (d.tickets === 1 ? 'it.' : 'them.');
  }

  /** True when the hidden tickets in the scope can be downloaded now. */
  canDownloadAttention(scope: AttentionExportScope): boolean {
    if (!this.attention || this.attentionDownloading !== null) {
      return false;
    }
    const count = scope === 'open' ? this.attention.hidden.open.total : this.attention.hidden.total;
    return count > 0;
  }

  /** Downloads the hidden tickets (open only, or all) as CSV. */
  downloadAttention(scope: AttentionExportScope): void {
    if (!this.canDownloadAttention(scope)) {
      return;
    }
    this.attentionDownloading = scope;
    this.runDownload(this.reassignSer.exportAttention(scope), 'hidden-tickets-' + scope + '.csv',
      'Unable to download the tickets with missing records.', () => this.attentionDownloading = null);
  }

  /** True when there are deleted accounts in the source list and the form is free. */
  canSelectDeleted(): boolean {
    return !this.busy && !this.sourcesLoading && this.sources.some(s => s.deleted);
  }

  /** Ticks every deleted account in the source picker up to the limit, clears the preview and scrolls the picker into view. */
  selectDeletedAccounts(): void {
    if (!this.canSelectDeleted()) {
      return;
    }
    for (const s of this.sources) {
      if (this.atSourceLimit()) {
        break;
      }
      if (s.deleted) {
        this.selectedIds.add(s.UserID);
      }
    }
    // Clear the search so the ticked rows are visible; it never unticks anything.
    this.sourceSearch = '';
    this.refreshVisibleSources();
    this.afterSourcesChanged();
    setTimeout(() => this.scrollToDeletedSources(), 0);
  }

  /** Brings the source picker into view and scrolls it to the deleted accounts group. */
  private scrollToDeletedSources(): void {
    const picker = document.getElementById('reassignSourceList');
    if (!picker) {
      return;
    }
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const heading = document.getElementById('reassignDeletedHeading');
    if (heading) {
      picker.scrollTop += heading.getBoundingClientRect().top - picker.getBoundingClientRect().top - 4;
    }
  }

  /** Index of the first row on the current page, counting from 1. */
  historyFirstRow(): number {
    const info = this.historyPageInfo;
    return info ? (this.historyPage - 1) * (info.per_page || HISTORY_PER_PAGE) + 1 : 0;
  }

  /** Index of the last row on the current page. */
  historyLastRow(): number {
    return this.history.length ? this.historyFirstRow() + this.history.length - 1 : 0;
  }

  /** True when the source account is ticked. */
  isSourceSelected(userId: number): boolean {
    return this.selectedIds.has(userId);
  }

  /** True when the maximum number of source accounts is ticked. */
  atSourceLimit(): boolean {
    return this.selectedIds.size >= MAX_SOURCES;
  }

  /** Ticks or unticks a source account, refusing to go past the limit. */
  toggleSource(source: IReassignSource, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      if (this.atSourceLimit()) {
        input.checked = false;
        return;
      }
      this.selectedIds.add(source.UserID);
    } else {
      this.selectedIds.delete(source.UserID);
    }
    this.afterSourcesChanged();
  }

  /** Ticks every source row the search currently shows, up to the limit. */
  selectAllShown(): void {
    let changed = false;
    for (const s of [...this.visibleRealSources, ...this.visibleDeletedSources]) {
      if (this.atSourceLimit()) {
        break;
      }
      if (!this.selectedIds.has(s.UserID)) {
        this.selectedIds.add(s.UserID);
        changed = true;
      }
    }
    if (changed) {
      this.afterSourcesChanged();
    }
  }

  /** Unticks every source account, including any hidden by the search. */
  clearSources(): void {
    if (this.selectedIds.size === 0) {
      return;
    }
    this.selectedIds.clear();
    this.afterSourcesChanged();
  }

  /** Drops a target that is now ticked as a source and discards the preview. */
  private afterSourcesChanged(): void {
    if (this.toUserId !== null && this.selectedIds.has(this.toUserId)) {
      this.toUserId = null;
    }
    this.clearPreview();
  }

  /** Applies the source search; it only hides rows and leaves the preview alone. */
  onSourceSearch(term: string): void {
    this.sourceSearch = term || '';
    this.refreshVisibleSources();
  }

  /** Recomputes the shown source rows from the search, split into real and deleted accounts. */
  private refreshVisibleSources(): void {
    const term = this.sourceSearch.trim().toLowerCase();
    const shown = this.sources.filter(s => this.sourceMatches(s, term));
    this.visibleRealSources = shown.filter(s => !s.deleted);
    this.visibleDeletedSources = shown.filter(s => s.deleted);
  }

  /** True when the source's name, email or ID contains the search term. */
  private sourceMatches(s: IReassignSource, term: string): boolean {
    if (!term) {
      return true;
    }
    const fields = s.deleted
      ? ['deleted account', String(s.UserID)]
      : [s.FirstName, s.LastName, this.fullName(s), this.sortName(s), s.EMail, String(s.UserID)];
    return fields.some(f => (f || '').toLowerCase().indexOf(term) !== -1);
  }

  /** Number of ticked sources the search is currently hiding. */
  hiddenSelectedCount(): number {
    const shown = new Set([...this.visibleRealSources, ...this.visibleDeletedSources].map(s => s.UserID));
    let hidden = 0;
    this.selectedIds.forEach(id => {
      if (!shown.has(id)) {
        hidden++;
      }
    });
    return hidden;
  }

  /** "3 accounts selected, 2,779 tickets", counting only the ticked statuses. */
  selectionSummary(): string {
    const count = this.selectedIds.size;
    if (count === 0) {
      return 'No accounts selected.';
    }
    const tickets = this.matchingCount() || 0;
    let text = this.formatCount(count) + ' ' + (count === 1 ? 'account' : 'accounts') + ' selected, ' +
      this.formatCount(tickets) + ' ' + this.ticketWord(tickets);
    const hidden = this.hiddenSelectedCount();
    if (hidden > 0) {
      text += ' (' + this.formatCount(hidden) + ' not shown by the search)';
    }
    return text + '.';
  }

  /** Handles a new "Assign to" choice. */
  onToChange(userId: number): void {
    this.toUserId = userId;
    this.clearPreview();
  }

  /** Ticks or unticks a status, refusing to untick the last one. */
  toggleStatus(option: IStatusOption, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.checked && this.checkedCount === 1 && option.checked) {
      input.checked = true;
      return;
    }
    option.checked = input.checked;
    this.clearPreview();
  }

  /** Statuses to send: null when all three are ticked, so unusual statuses move too. */
  selectedStatuses(): ReassignStatus[] | null {
    if (this.checkedCount === this.statusOptions.length) {
      return null;
    }
    return this.statusOptions.filter(o => o.checked).map(o => o.value);
  }

  /** Tickets the ticked sources have in the ticked statuses, per the sources list; null when none is ticked. */
  matchingCount(): number | null {
    const chosen = this.selectedSources();
    if (chosen.length === 0) {
      return null;
    }
    const statuses = this.selectedStatuses();
    return chosen.reduce((sum, s) => sum + this.countForStatuses(s, statuses), 0);
  }

  /** One source's tickets in the given statuses; null statuses means every ticket. */
  private countForStatuses(source: IReassignSource, statuses: ReassignStatus[] | null): number {
    if (!source.counts) {
      return 0;
    }
    if (statuses === null) {
      return source.counts.total || 0;
    }
    const byStatus: { [status: number]: number } = {
      1: source.counts.new,
      2: source.counts.active,
      3: source.counts.closed
    };
    return statuses.reduce((sum, st) => sum + (byStatus[st] || 0), 0);
  }

  /** The reason with surrounding whitespace removed, as it will be sent. */
  trimmedReason(): string {
    return (this.reason || '').trim();
  }

  /** True when the trimmed reason is between REASON_MIN and REASON_MAX characters. */
  reasonValid(): boolean {
    const length = this.trimmedReason().length;
    return length >= REASON_MIN && length <= REASON_MAX;
  }

  /** True when sources and a target are chosen, there is something to move and nothing is running. */
  canPreview(): boolean {
    return this.selectedIds.size > 0 && this.selectedIds.size <= MAX_SOURCES && this.toUserId !== null &&
      !this.selectedIds.has(this.toUserId) && this.checkedCount > 0 && this.matchingCount() !== 0 &&
      !this.previewLoading && !this.busy;
  }

  /** Requests a preview for the current selection. */
  runPreview(): void {
    if (!this.canPreview()) {
      return;
    }
    const request: IReassignRequest = {
      from_user_ids: this.selectedSources().map(s => s.UserID),
      to_user_id: this.toUserId,
      statuses: this.selectedStatuses()
    };
    this.clearPreview();
    const seq = this.previewSeq;
    this.previewLoading = true;
    this.reassignSer.preview(request)
      .pipe(finalize(() => {
        if (seq === this.previewSeq) {
          this.previewLoading = false;
        }
      }))
      .subscribe({
        next: (data) => {
          if (seq !== this.previewSeq) {
            return;
          }
          if (!this.previewMatches(data, request)) {
            this.previewError = 'The preview did not match the selected accounts. Please preview again.';
            return;
          }
          this.preview = data;
          this.previewRequest = request;
        },
        error: (err) => {
          if (seq !== this.previewSeq) {
            return;
          }
          this.previewError = this.serverMessage(err, 'Unable to preview this reassignment.');
        }
      });
  }

  /** True when a preview response covers exactly the requested sources and target. */
  private previewMatches(data: IReassignPreview, request: IReassignRequest): boolean {
    if (!data || !data.to || !Array.isArray(data.sources) || data.to.UserID !== request.to_user_id) {
      return false;
    }
    const returned = data.sources.map(s => s.UserID).sort((a, b) => a - b);
    const wanted = [...request.from_user_ids].sort((a, b) => a - b);
    return returned.length === wanted.length && returned.every((id, i) => id === wanted[i]);
  }

  /** True when any previewed source, or the combined counts, has tickets with an unusual status. */
  previewHasOther(): boolean {
    return !!this.preview && (this.preview.counts.other > 0 || this.preview.sources.some(s => s.counts && s.counts.other > 0));
  }

  /** Discards any preview so a stale one can never be confirmed. */
  clearPreview(): void {
    this.previewSeq++;
    this.preview = null;
    this.previewRequest = null;
    this.previewLoading = false;
    this.previewError = null;
  }

  /** Asks the user to confirm the previewed reassignment, then commits it. */
  confirmCommit(): void {
    const reason = this.trimmedReason();
    if (!this.preview || !this.previewRequest || this.busy || this.preview.counts.total === 0 || !this.reasonValid()) {
      return;
    }
    const preview = this.preview;
    const request = this.previewRequest;
    const seq = this.previewSeq;
    const total = preview.counts.total;

    this.awaitingConfirm = true;
    const modalRef = this.modalService.open(ModalComponent);
    modalRef.componentInstance.title = 'Confirm Ticket Reassignment';
    modalRef.componentInstance.okBtnTitle = 'Reassign';
    modalRef.componentInstance.cancelBtnTitle = 'Cancel';
    modalRef.componentInstance.confirmationStr =
      this.confirmHeadline(preview, total) +
      '<p>Tickets included: ' + this.escapeHtml(this.statusesLabel(request.statuses)) + '.</p>' +
      '<p>Reason: ' + this.escapeHtml(reason) + '</p>' +
      '<p>' + this.escapeHtml(this.fullName(preview.to)) + ' will be emailed a summary of this reassignment.</p>' +
      '<p>This can be undone from the history on this page. Tickets reassigned again in the meantime are not undone.</p>';
    modalRef.result.then(() => {
      this.awaitingConfirm = false;
      if (seq !== this.previewSeq) {
        return;
      }
      this.commit(preview, request, reason);
    })
    .catch(() => {
      this.awaitingConfirm = false;
    });
  }

  /** Opening lines of the confirmation: today's sentence for one source, otherwise a count plus the first sources. */
  private confirmHeadline(preview: IReassignPreview, total: number): string {
    const target = '<span class="text-primary">' + this.escapeHtml(this.fullName(preview.to)) + '</span> (' +
      this.escapeHtml(preview.to.EMail || 'no email') + ')';
    const tickets = this.formatCount(total) + ' ' + this.ticketWord(total);
    if (preview.sources.length === 1) {
      const s = preview.sources[0];
      return '<p><strong>Move ' + tickets + ' from ' + this.sourceConfirmLabel(s) + ' to ' + target + '?</strong></p>';
    }
    const shown = preview.sources.slice(0, CONFIRM_LIST_LIMIT);
    const rest = preview.sources.length - shown.length;
    return '<p><strong>Move ' + tickets + ' from ' + this.formatCount(preview.sources.length) + ' accounts to ' +
      target + '?</strong></p>' +
      '<ul>' + shown.map(s => {
        const n = s.counts ? s.counts.total : 0;
        return '<li>' + this.sourceConfirmLabel(s) + ': ' + this.formatCount(n) + ' ' + this.ticketWord(n) + '</li>';
      }).join('') + '</ul>' +
      (rest > 0 ? '<p>and ' + this.formatCount(rest) + ' more</p>' : '');
  }

  /** Escaped source description for the confirmation: name, email, role and state, or the deleted account ID. */
  private sourceConfirmLabel(s: IReassignSource): string {
    if (s.deleted) {
      return '<span class="text-primary">' + this.escapeHtml(this.deletedLabel(s.UserID)) + '</span>';
    }
    return '<span class="text-primary">' + this.escapeHtml(this.fullName(s)) + '</span> (' +
      this.escapeHtml(s.EMail || 'no email') + ', ' + this.escapeHtml(this.roleName(s.RoleType)) + ', ' +
      (this.isActive(s) ? 'Active' : 'Inactive') + ')';
  }

  /** Sends the commit with the previewed total so the server can refuse if it changed. */
  private commit(preview: IReassignPreview, request: IReassignRequest, reason: string): void {
    this.committing = true;
    this.reassignSer.commit({ ...request, expected_total: preview.counts.total, reason })
      .pipe(finalize(() => this.committing = false))
      .subscribe({
        next: (result) => {
          this.displayMessage('Reassignment Complete', this.commitSummary(preview, result) + ' ' +
            this.commitEmailSentence(result));
          this.resetForm();
          this.loadSources();
          this.historyPage = 1;
          this.loadHistory();
          this.loadAttention();
        },
        error: (err) => {
          if (this.httpStatus(err) === 409) {
            this.clearPreview();
            this.displayMessage('Reassignment Not Completed',
              this.serverMessage(err, 'The number of tickets changed since the preview. Please preview again.') +
              ' ' + REFRESHED_NOTE);
            this.refreshLists();
            return;
          }
          this.displayMessage('Reassignment Not Completed', this.serverMessage(err, 'Unable to reassign the tickets.'));
        }
      });
  }

  /** "N tickets moved from <source> to <target>." for one source, otherwise "from K accounts". */
  private commitSummary(preview: IReassignPreview, result: IReassignCommitResult): string {
    const tickets = this.formatCount(result.moved) + ' ' + this.ticketWord(result.moved);
    const target = this.fullName(preview.to) + ' (' + (preview.to.EMail || 'no email') + ')';
    if (preview.sources.length === 1) {
      return tickets + ' moved from ' + this.sourcePlainLabel(preview.sources[0]) + ' to ' + target + '.';
    }
    const batches = Array.isArray(result.batches) ? result.batches.filter(b => b.moved > 0).length : 0;
    const accounts = batches || preview.sources.length;
    return tickets + ' moved from ' + this.formatCount(accounts) + ' ' + (accounts === 1 ? 'account' : 'accounts') +
      ' to ' + target + '.';
  }

  /** Plain-text source description: "Name (email)" or "Deleted account (ID n)". */
  private sourcePlainLabel(s: IReassignSource): string {
    return s.deleted ? this.deletedLabel(s.UserID) : this.fullName(s) + ' (' + (s.EMail || 'no email') + ')';
  }

  /** "Deleted account (ID n)". */
  deletedLabel(userId: number): string {
    return 'Deleted account (ID ' + userId + ')';
  }

  /** Escaped "Name (email)" for the source of a history row, or the deleted account ID. */
  private historyFromConfirmLabel(batch: IReassignBatch): string {
    if (batch.from_deleted || !batch.from_user) {
      return '<span class="text-primary">' + this.escapeHtml(this.deletedLabel(batch.from_user_id)) + '</span>';
    }
    return '<span class="text-primary">' + this.escapeHtml(this.fullName(batch.from_user)) + '</span> (' +
      this.escapeHtml(batch.from_user.EMail || 'no email') + ')';
  }

  /** Asks the user to confirm undoing a batch, then undoes it. */
  confirmUndo(batch: IReassignBatch): void {
    if (this.busy || batch.undone_at) {
      return;
    }
    this.awaitingConfirm = true;
    const modalRef = this.modalService.open(ModalComponent);
    modalRef.componentInstance.title = 'Undo Ticket Reassignment';
    modalRef.componentInstance.okBtnTitle = 'Undo';
    modalRef.componentInstance.cancelBtnTitle = 'Cancel';
    modalRef.componentInstance.confirmationStr =
      '<p><strong>Move the ' + this.formatCount(batch.moved) + ' ' + this.ticketWord(batch.moved) + ' in this batch back from ' +
      '<span class="text-primary">' + this.escapeHtml(this.fullName(batch.to_user)) + '</span> (' +
      this.escapeHtml(batch.to_user?.EMail || 'no email') + ') to ' +
      this.historyFromConfirmLabel(batch) + '?</strong></p>' +
      '<p>Tickets that have been reassigned since will be left where they are.</p>' +
      '<p>The receiving attorney will be emailed that the tickets were moved back.</p>';
    modalRef.result.then(() => {
      this.awaitingConfirm = false;
      this.undo(batch);
    })
    .catch(() => {
      this.awaitingConfirm = false;
    });
  }

  /** Calls undo for a batch and reports what was restored. */
  private undo(batch: IReassignBatch): void {
    this.undoingId = batch.id;
    this.reassignSer.undo(batch.id)
      .pipe(finalize(() => this.undoingId = null))
      .subscribe({
        next: (result) => {
          let msg = this.formatCount(result.restored) + ' restored';
          if (result.skipped > 0) {
            msg += ', ' + this.formatCount(result.skipped) + ' skipped because they were reassigned since';
          }
          const emailSentence = this.undoEmailSentence(result);
          this.displayMessage('Undo Complete', msg + '.' + (emailSentence ? ' ' + emailSentence : ''));
          this.clearPreview();
          this.loadSources();
          this.loadHistory();
          this.loadAttention();
        },
        error: (err) => {
          const refused = this.httpStatus(err) === 409;
          this.displayMessage('Undo Not Completed', this.serverMessage(err, 'Unable to undo this reassignment.') +
            (refused ? ' ' + REFRESHED_NOTE : ''));
          if (refused) {
            this.refreshLists();
          }
        }
      });
  }

  /** Reloads accounts, attorneys, history and attention counts after a refusal, since someone else changed things since this page loaded. */
  private refreshLists(): void {
    this.loadSources();
    this.loadTargets();
    this.historyPage = 1;
    this.loadHistory();
    this.loadAttention();
  }

  /** Puts the form back to its initial state. */
  private resetForm(): void {
    this.selectedIds.clear();
    this.sourceSearch = '';
    this.refreshVisibleSources();
    this.toUserId = null;
    this.statusOptions = this.defaultStatusOptions();
    this.reason = '';
    this.clearPreview();
  }

  /** Sentence reporting whether the commit summary email reached the receiving attorney. */
  private commitEmailSentence(result: IReassignCommitResult): string {
    const recipient = result.email_to || 'the receiving attorney';
    if (result.email_sent) {
      return 'A summary was emailed to ' + recipient + '.';
    }
    return 'The tickets were moved, but the summary email to ' + recipient +
      ' could not be sent. Please let them know directly.';
  }

  /** Sentence reporting the undo email, or empty when no email was attempted. */
  private undoEmailSentence(result: IReassignUndoResult): string {
    if (!result.email_to) {
      return '';
    }
    if (result.email_sent) {
      return result.email_to + ' was emailed.';
    }
    return 'The tickets were moved back, but the email to ' + result.email_to +
      ' could not be sent. Please let them know directly.';
  }

  /** All three statuses ticked. */
  private defaultStatusOptions(): IStatusOption[] {
    return [
      { value: 1, label: 'New', checked: true },
      { value: 2, label: 'Active', checked: true },
      { value: 3, label: 'Closed', checked: true }
    ];
  }

  /** Row label for a source account; deleted accounts show their ID. */
  sourceLabel(s: IReassignSource): string {
    const total = s.counts ? s.counts.total : 0;
    if (s.deleted) {
      return this.deletedLabel(s.UserID) + ' | ' + this.formatCount(total) + ' ' + this.ticketWord(total);
    }
    return this.sortName(s) + ' | ' + (s.EMail || 'no email') + ' | ' + this.roleName(s.RoleType) +
      ' (' + (this.isActive(s) ? 'Active' : 'Inactive') + ') | ' + this.formatCount(total) + ' ' + this.ticketWord(total);
  }

  /** Option label for a target attorney. */
  targetLabel(t: IReassignUserRef): string {
    return this.sortName(t) + ' | ' + (t.EMail || 'no email');
  }

  /** Display name for a RoleType. */
  roleName(roleType: number): string {
    return ROLE_NAMES[roleType] || 'Role ' + roleType;
  }

  /** True when the account is active, whether the API sends 1 or true. */
  isActive(u: { Active: number | boolean | null }): boolean {
    return !!u && Number(u.Active) === 1;
  }

  /** "First Last", falling back to the user id when the name is blank. */
  fullName(u: IReassignUserRef): string {
    if (!u) {
      return '';
    }
    const name = ((u.FirstName || '') + ' ' + (u.LastName || '')).trim();
    return name || 'User ' + u.UserID;
  }

  /** "Last, First", falling back to the user id when the name is blank. */
  sortName(u: IReassignUserRef): string {
    const parts = [u.LastName, u.FirstName].filter(p => !!p && p.trim() !== '');
    return parts.length ? parts.join(', ') : 'User ' + u.UserID;
  }

  /** Readable list of the statuses a request covers. */
  statusesLabel(statuses: ReassignStatus[] | null): string {
    if (statuses === null) {
      return 'All statuses';
    }
    return statuses.map(s => STATUS_NAMES[s]).join(', ');
  }

  /** Readable status filter for a history row, which the API sends as a string or null. */
  historyStatusesLabel(statuses: string | null): string {
    if (statuses === null || statuses === undefined || statuses === '') {
      return 'All';
    }
    const codes = statuses.match(/\d+/g);
    if (!codes) {
      return statuses;
    }
    return codes.map(c => STATUS_NAMES[Number(c)] || 'Status ' + c).join(', ');
  }

  /** Number with thousands separators. */
  formatCount(n: number): string {
    return (n || 0).toLocaleString('en-US');
  }

  /** "ticket" or "tickets" for a count. */
  ticketWord(n: number): string {
    return n === 1 ? 'ticket' : 'tickets';
  }

  /** HTTP status from an error, unwrapping the AuthInterceptor's {status: false, error} wrapper. */
  private httpStatus(err: any): number | null {
    const httpErr = err && err.status === false ? err.error : err;
    return httpErr && typeof httpErr.status === 'number' ? httpErr.status : null;
  }

  /** Server's {message} from an error body, unwrapping the AuthInterceptor's wrapper. */
  private serverMessage(err: any, fallback: string): string {
    const httpErr = err && err.status === false ? err.error : err;
    const body = httpErr ? httpErr.error : null;
    const text = body && typeof body.message === 'string' && body.message.trim() !== '' ? body.message.trim() : fallback;
    // Server messages do not all end with a full stop, and another sentence may be appended after this one.
    return /[.!?]$/.test(text) ? text : text + '.';
  }

  /** Escapes text for the modals, which render their message as HTML. */
  private escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Shows a plain-text message in the shared information modal. */
  private displayMessage(title: string, text: string): void {
    const modalRef = this.modalService.open(InfoModalComponent);
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.confirmationStr = '<p>' + this.escapeHtml(text) + '</p>';
    modalRef.result.then(() => {}).catch(() => {});
  }
}
